import {
  Box,
  Button,
  Dialog,
  Menu,
  MenuItem,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Magnifier,
  GlassMagnifier,
  SideBySideMagnifier,
  PictureInPictureMagnifier,
  MOUSE_ACTIVATION,
  TOUCH_ACTIVATION
} from 'react-image-magnifiers';
import { ObjectView } from 'components/Avaliations';
import { SelectableButton } from 'components/BullSearch';
import { Hr, Img, Space } from 'components/common';
import { objectFilter } from 'object-fn-ts/lib';
import { actions, Store } from 'rdx';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bull } from 'types';
import utils from 'utils';
import styles from './BullDetails.styles';
import './BullDetails.css';
import Measurements from './Measurements';
import Pedigree from './Pedigree';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

import ZLBullDetails from './Avaliations/ZL-bull-detail';
import StampList from './StampList';
import TextInfo from './TextInfo';
import React from 'react';
import ECBullDetails from './Avaliations/EC-bull-detail';
import ELBullDetails from './Avaliations/EL-bull-detail';
import ZCBullDetails from './Avaliations/ZC-bull-detail';
import { PDFDocument } from 'pdf-lib';


type Props = {
  bull: Bull;
};

function hasCarcaçaAvaliation(arr: { [k: string]: any }[]) {
  return arr.some((o) =>
    Object.entries(o)[0][0].toLowerCase().includes('carcaça')
  );
}

function maxStrLen(s: string, max = 0) {
  return s.length > max ? s.slice(0, max - 3) + '...' : s;
}

function reorderObject(obj: any, order: string[]): any {
  return Object.fromEntries(order.map((key) => [ key, obj[key] ])) as any;
}

const BullDetails: FC<Props> = ({ bull }) => {
  const workableProofs = [ ...bull.proof ];
  for (let i = 0; i < bull.proof.length; i++) {
    const currentProof = bull.proof[i];
    const pProofKeys = Object.keys(currentProof).map((k) => k);
    let pHasCarcaca = false;
    let pCarcacaIndex = -1;
    for (let j = 0; j < pProofKeys.length; j++) {
      if (pProofKeys[j].toLocaleLowerCase().indexOf('carcaça') != -1) {
        pHasCarcaca = true;
        pCarcacaIndex = j;
        break;
      }
    }
    if (pCarcacaIndex != -1) {
      const carcacaKey = pProofKeys[pCarcacaIndex];
      pProofKeys.splice(pCarcacaIndex, 1);
      pProofKeys.splice(0, 0, carcacaKey);
      workableProofs[i] = reorderObject(bull.proof[i], pProofKeys);
    }
  }
  const dispatch = useDispatch();
  const items = useSelector((store: Store) => store.items);
  const targetIndex = items.bulls.findIndex((b) => b.CRVcode === bull.CRVcode);
  const selected = items.selectedBulls.includes(targetIndex);
  const gtMd = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [ videoModalOpen, setVideoModalOpen ] = useState(false);
  const [ selectedProof, setSelectedProof ] = useState<string>(
    Array.isArray(workableProofs) && workableProofs.length > 0
      ? (() => {
        const name1 = Object.entries(workableProofs[0])[0][0];
        const name2 = (Object.entries(workableProofs[0])[1] ?? '')[0];
        if (
          name1.toLowerCase().includes('carcaça') &&
            name2 !== null &&
            name2 !== undefined &&
            name2 !== ''
        )
          return name2;
        return name1;
      })()
      : ' '
  );
  const [ hasCarcaça, setHasCarcaça ] = useState<boolean>(false);
  const [ carcaçaName, setCarcaçaName ] = useState('');
  const [ selectedProofData, setSelectedProofData ] = useState<any>([]);
  const [ downloadPdfButtonContent, setDownloadPdfButtonContent ] = useState<
    string | JSX.Element
  >('Baixar PDF');
  const proofObject = objectFilter(
    workableProofs[0] || {},
    (key) => !String(key).toLowerCase().includes('carcaça')
  );

  const [ anchorEl, setAnchorEl ] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const openPDF = async (language: string) => {
    handleClose();

    const lang = language;
  
    if (!language || language === 'pt') {
      language = '';
    } else {
      language = '_' + language;
    }
  
    const pdfToMerge = await PDFDocument.create();

    // Adiciona o PDF principal depois
    try {
      const mainPdfUrl = `${process.env.REACT_APP_API}/bull/downloadpdf/${bull.warName}.pdf?url=${bull.pdf?.replace('.pdf', language + '.pdf')}`;
      const mainResponse = await fetch(mainPdfUrl);
      const mainBuffer = await mainResponse.arrayBuffer();
      const mainPdf = await PDFDocument.load(mainBuffer);
      const mainPages = await pdfToMerge.copyPages(mainPdf, mainPdf.getPageIndices());
      for (const page of mainPages) {
        pdfToMerge.addPage(page);
      }
    } catch (err) {
      console.error('Erro ao carregar PDF principal:', err);
    }
  
    // Gera e baixa o PDF final
    const mergedPdfBytes = await pdfToMerge.save();
    const blob = new Blob([ mergedPdfBytes.buffer ], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
  
    const a = document.createElement('a');
    a.href = url;
    a.download = bull.warName + '.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };  

  useEffect(() => {
    try {
      const segment = utils.segmentFromBullOrBreed(bull);
      if (segment) {
        setHasCarcaça(hasCarcaçaAvaliation(workableProofs));
        const carcaçaAvaliation = workableProofs.find((p) =>
          Object.keys(p)[0].toLowerCase().includes('carcaça')
        );
        if (carcaçaAvaliation) {
          const carcaçaAvaliationObject = objectFilter(
            carcaçaAvaliation,
            (key) => String(key).toLowerCase().includes('carcaça')
          );
          const avaliationName = Object.keys(carcaçaAvaliationObject)[0];
          setCarcaçaName(avaliationName);
        }
      }
      if (
        selectedProof in proofObject &&
        !selectedProof.toLowerCase().includes('carcaça')
      ) {
        //TODO: corrigir caso dê problema
        if (segment) {
          const proofBuilder =
            utils.proofBuilders[
              `build${segment.toUpperCase()}` as keyof typeof utils.proofBuilders
            ];
          const data = proofBuilder(proofObject, selectedProof);
          setSelectedProofData(null);
          setTimeout(() => setSelectedProofData(data), 10);
        }
      } else {
        const carcaçaAvaliation = workableProofs.find((p) =>
          Object.keys(p)[0].toLowerCase().includes('carcaça')
        );
        if (carcaçaAvaliation) {
          const carcaçaAvaliationObject = objectFilter(
            carcaçaAvaliation,
            (key) => String(key).toLowerCase().includes('carcaça')
          );

          const carcaçaKey = Object.keys(carcaçaAvaliation)[0];

          const obj: any = {};

          for (
            let i = 0;
            i < carcaçaAvaliation[carcaçaKey]?.table.length;
            i++
          ) {
            obj[carcaçaAvaliation[carcaçaKey]?.table[i].name] =
              carcaçaAvaliation[carcaçaKey]?.table[i].values[0];
          }
          setSelectedProofData(obj);
        }
      }
    } catch (err) {
      console.log('RENDERING ERROR', { err });
      setSelectedProofData(null);
    }
  }, [ selectedProof, bull.proof ]);

  type ProofData = {
    proof?: any;
    hasCarcaca?: boolean;
    carcacaName?: string;
  };

  const getProofData = (proofkey: any, bull: Bull): ProofData | null => {
    try {
      let profData: ProofData = {};

      const segment = utils.segmentFromBullOrBreed(bull);
      if (segment) {
        profData = {
          ...profData,
          hasCarcaca: hasCarcaçaAvaliation(workableProofs),
        };

        const carcaçaAvaliation = workableProofs.find((p) =>
          Object.keys(p)[0].toLowerCase().includes('carcaça')
        );
        if (carcaçaAvaliation) {
          const carcaçaAvaliationObject = objectFilter(
            carcaçaAvaliation,
            (key) => String(key).toLowerCase().includes('carcaça')
          );
          const avaliationName = Object.keys(carcaçaAvaliationObject)[0];

          profData = { ...profData, carcacaName: avaliationName };
        }
      }

      if (
        proofkey in proofObject &&
        !proofkey.toLowerCase().includes('carcaça')
      ) {
        if (segment) {
          const proofBuilder =
            utils.proofBuilders[
              `build${segment.toUpperCase()}` as keyof typeof utils.proofBuilders
            ];
          const data = proofBuilder(proofObject, proofkey);
          profData = { ...profData, proof: data };
        }
      } else {
        const carcaçaAvaliation = workableProofs.find((p) =>
          Object.keys(p)[0].toLowerCase().includes('carcaça')
        );
        if (carcaçaAvaliation) {
          const carcaçaAvaliationObject = objectFilter(
            carcaçaAvaliation,
            (key) => String(key).toLowerCase().includes('carcaça')
          );

          const carcaçaKey = Object.keys(carcaçaAvaliation)[0];

          const obj: any = {};

          for (
            let i = 0;
            i < carcaçaAvaliation[carcaçaKey]?.table.length;
            i++
          ) {
            obj[carcaçaAvaliation[carcaçaKey]?.table[i].name] =
              carcaçaAvaliation[carcaçaKey]?.table[i].values[0];
          }
          profData = { ...profData, proof: obj };
        }
      }
      return profData;
    } catch (err) {
      console.log('RENDERING ERROR', { err });
      return null;
    }
  };

  useEffect(() => {
    window.location.replace('crv-app://search/' + bull.CRVcode);
  }, []);

  const regex = /(?:v=([^&]+))|(?:youtu.be\/([^?]+))/;
  const videoId =
    regex.exec(bull?.youtube || '')?.[1] ||
    regex.exec(bull?.youtube || '')?.[2];

  const segmentPathlike = utils
    .segmentTranslator(utils.segmentFromBullOrBreed(bull), 'pathlike')
    .replace('-', '_');
  const computedMainImg =
    `https://firebasestorage.googleapis.com/v0/b/crv-lagoa.appspot.com/o/${segmentPathlike}%2Fbulls%2F${bull.CRVcode}.jpg?alt=media`;
  const computedMainImgClean = computedMainImg.replace(/\s/g, '');
  const mainImgSrc = bull.images?.[0]?.filepath || bull.picture || computedMainImgClean;
  const [ mainImgOk, setMainImgOk ] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const img = new Image();
    img.onload = () => isMounted && setMainImgOk(true);
    img.onerror = () => isMounted && setMainImgOk(false);
    img.src = mainImgSrc;
    return () => {
      isMounted = false;
    };
  }, [ mainImgSrc ]);

  return (
    <Stack
      {...utils.stack.flex.col('center', 'flex-start')}
      spacing={gtMd ? 4 : 2}
      sx={styles.container}
    >
      <Stack
        {...utils.stack.flex.row('space-between', 'flex-start')}
        sx={utils.sx.fw}
      >
        <Stack
          {...utils.stack.flex.col('flex-start', 'flex-start')}
          spacing={1}
        >
          <Typography variant="h2" translate="no">
            {bull?.warName != '-' ? bull?.warName : bull.name}
          </Typography>
          <Typography>{bull?.registry || 'S/R'}</Typography>
        </Stack>
        <Stack {...utils.stack.flex.col('flex-end', 'flex-start')} spacing={2}>
          {'ecommerce' in bull &&
            typeof bull.ecommerce === 'string' &&
            bull.ecommerce !== '-' && (
            <Button
              onClick={() => window.open(bull.ecommerce, '_blank')}
              sx={{ minWidth: '144px' }}
            >
                Comprar
            </Button>
          )}
          {'pdf' in bull &&
            typeof bull.pdf === 'string' &&
            !bull.pdf.includes('%2F-') && (
            <Button
              onClick={handleClick}
              sx={styles.downloadButton}
              variant="outlined"
            >
              <Stack
                {...utils.stack.flex.row('center', 'center')}
                spacing={1}
              >
                {downloadPdfButtonContent === 'Baixar PDF' ? (
                  <Img url="/icons/cloud-download.svg" />
                ) : null}

                <Typography
                  sx={(theme) => ({
                    fontSize: '0.875rem !important',
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  })}
                >
                  {downloadPdfButtonContent}
                </Typography>
              </Stack>
            </Button>
          )}
        </Stack>
      </Stack>
      <Menu
        id="lock-menu"
        MenuListProps={{
          'aria-labelledby': 'lock-button',
          role: 'listbox',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: 144,
          },
        }}
      >
        <MenuItem onClick={() => openPDF('pt')}>Português</MenuItem>
        <MenuItem key={1} onClick={() => openPDF('en')}>
          English
        </MenuItem>
        <MenuItem key={1} onClick={() => openPDF('es')}>
          Español
        </MenuItem>
      </Menu>
      <Stack
        {...utils.stack.flex.row('flex-start', 'flex-start')}
        spacing={4}
        sx={utils.sx.fw}
      >
        <Stack
          {...utils.stack.flex.col('center', 'flex-start')}
          spacing={3}
          sx={styles.leftSubContainer}
        >
          <Stack
            {...utils.stack.flex.col('center', 'center')}
            sx={styles.mainImgWrapper}
          >
            <Stack
              {...utils.stack.flex.col('flex-start', 'center')}
              sx={{ ...styles.mainImgWrapper, height: '0px' }}
            >
              {bull.newRelease === 'X' && (
                <Img
                  url="/imgs/badge-lancamento.svg"
                  sx={{
                    width: '100px', 
                    position: 'relative',
                    top: '45px',
                    zIndex: '1'
                  }}
                />
              )}
            </Stack>

            {mainImgOk ? (
              <SideBySideMagnifier
                imageAlt="Foto Touro"
                className='side-by-side-magnifier'
                imageSrc={mainImgSrc}
              />
            ) : (
              <Img
                url="/imgs/default.png"
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            )}
           
            <Space size={2} />
            <SelectableButton
              selected={selected}
              onClick={() =>
                dispatch(
                  actions.items[selected ? 'unselectBull' : 'selectBull'](
                    targetIndex
                  )
                )
              }
            />
            <Space size={2} />
          </Stack>
          {gtMd && (
            <Stack
              {...utils.stack.flex.row('flex-start', 'center')}
              spacing={1}
              sx={{ width: '100%', overflowX: 'auto' }}
            >
              {bull.images.slice(1).map((img) => {
                return (
                  <Tooltip
                    title={img.photoCaption ?? ''}
                    arrow
                    key={Math.random()}
                  >
                    <Stack>
                      <Img
                        sx={{
                          width: '120px',
                          aspectRatio: '3/2',
                          cursor: 'pointer',
                        }}
                        url={img.filepath}
                        fallbackImg="/imgs/default.png"
                        rmIfFail
                        onClick={() => {
                          dispatch(actions.modal.setSIB_URL(img.filepath));
                          dispatch(actions.modal.setSIBOpen(true));
                          dispatch(
                            actions.modal.setSIBSubtitle(
                              (img.photoCaption || '').trim()
                            )
                          );
                        }}
                      />
                      {img.photoCaption &&
                        img.photoCaption.trim() &&
                        img.photoCaption.trim() !== '-' && (
                        <Typography
                          sx={(t) => ({
                            width: '100%',
                            background: t.palette.primary.main,
                            color: 'white',
                            fontSize: '10px !important',
                          })}
                        >
                          {maxStrLen(img.photoCaption.trim(), 25)}
                        </Typography>
                      )}
                    </Stack>
                  </Tooltip>
                );
              })}

              {bull?.youtube && (
                <Tooltip title={''} arrow>
                  <Stack>
                    <Img
                      sx={{
                        width: '120px',
                        aspectRatio: '3/2',
                        cursor: 'pointer',
                      }}
                      url={`https://i3.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
                      fallbackImg="/imgs/default.png"
                      rmIfFail
                      onClick={() => {
                        setVideoModalOpen(true);
                      }}
                    />

                    <Typography
                      sx={(t) => ({
                        width: '100%',
                        background: t.palette.primary.main,
                        color: 'white',
                        fontSize: '10px !important',
                      })}
                    >
                      Video
                    </Typography>
                  </Stack>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
        {gtMd && (
          <Stack
            {...utils.stack.flex.col('flex-start', 'flex-start')}
            sx={{ width: '50%', maxWidth: '50%' }}
          >
            <StampList
              list={bull.stamps}
              segment={utils.segmentFromBullOrBreed(bull)}
              crvCode={bull.CRVcode}
            />
            <TextInfo bull={bull} />
          </Stack>
        )}
      </Stack>
      {!gtMd && (
        <Stack
          {...utils.stack.flex.row('flex-start', 'center')}
          spacing={1}
          sx={{ width: '100%', overflowX: 'auto' }}
        >
          <Tooltip title={''} arrow>
            <Stack>
              <Img
                sx={{
                  width: '120px',
                  aspectRatio: '3/2',
                  cursor: 'pointer',
                }}
                url={'https://i3.ytimg.com/vi/iHkGoJ0w_VU/maxresdefault.jpg'}
                fallbackImg="/imgs/default.png"
                rmIfFail
                onClick={() => {
                  setVideoModalOpen(true);
                }}
              />

              <Typography
                sx={(t) => ({
                  width: '100%',
                  background: t.palette.primary.main,
                  color: 'white',
                  fontSize: '10px !important',
                })}
              >
                Video
              </Typography>
            </Stack>
          </Tooltip>
          {bull.images.slice(1).map((img, i, arr) => {
            return (
              <Tooltip title="">
                <Stack>
                  <Img
                    sx={{ width: '120px', aspectRatio: '3/2' }}
                    url={img.filepath}
                    fallbackImg="/imgs/default.png"
                    rmIfFail
                    onClick={() => {
                      dispatch(actions.modal.setSIB_URL(img.filepath));
                      dispatch(actions.modal.setSIBOpen(true));
                      dispatch(
                        actions.modal.setSIBSubtitle(
                          (img.photoCaption || '').trim()
                        )
                      );
                    }}
                  />
                  {img.photoCaption &&
                    img.photoCaption.trim() &&
                    img.photoCaption.trim() !== '-' && (
                    <Typography
                      sx={(t) => ({
                        width: '100%',
                        background: t.palette.primary.main,
                        color: 'white',
                        fontSize: '10px !important',
                      })}
                    >
                      {maxStrLen(img.photoCaption.trim(), 25)}
                    </Typography>
                  )}
                </Stack>
              </Tooltip>
            );
          })}
        </Stack>
      )}
      {!gtMd && (
        <StampList
          list={bull.stamps}
          segment={utils.segmentFromBullOrBreed(bull)}
          crvCode={bull.CRVcode}
        />
      )}
      <Space size={1} />
      {!gtMd && <TextInfo bull={bull} />}
      <Space size={1} />
      <Hr />
      <Space size={1} />
      <Dialog
        onClose={() => setVideoModalOpen(false)}
        fullWidth={true}
        open={videoModalOpen}
      >
        <iframe
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '3/2',
            borderWidth: '0px',
          }}
          src={'https://youtube.com/embed/' + videoId}
          allow="autoplay; encrypted-media"
          allowFullScreen
          title="video"
        />
      </Dialog>

      {/* corte europeu */}
      {utils.segmentFromBullOrBreed(bull) === 'ec' &&
        workableProofs
          .map((x) => Object.entries(x))
          .flat()
          .map(([ key ]) => {
            const proofData = getProofData(key, bull);
            return (
              <ECBullDetails
                key={key}
                bull={bull}
                selectedProof={key}
                selectedProofData={proofData?.proof}
                carcacaName={proofData?.carcacaName}
                hasCarcaca={proofData?.hasCarcaca}
                proofObject={proofObject}
              />
            );
          })}

      {/* leite europeu */}
      {utils.segmentFromBullOrBreed(bull) === 'el' &&
        Object.entries(proofObject).map(([ key ]) => {
          const proofData = getProofData(key, bull);
          console.log('teste'+proofData);
          return (
            <ELBullDetails
              key={key}
              bull={bull}
              selectedProof={key}
              selectedProofData={proofData?.proof}
            />
          );
        })}

      {/* leite-zebu  */}
      {utils.segmentFromBullOrBreed(bull) === 'zl' &&
        Object.entries(proofObject).map(([ key ]) => {
          const proofData = getProofData(key, bull);
          return (
            <ZLBullDetails
              key={key}
              bull={bull}
              selectedProof={key}
              selectedProofData={proofData?.proof}
              proofObject={proofObject}
            />
          );
        })}

      {/* corte-zebu */}
      {utils.segmentFromBullOrBreed(bull) === 'zc' &&
        workableProofs
          .map((x) => Object.entries(x))
          .flat()
          .map(([ key ]) => {
            const proofData = getProofData(key, bull);
            return (
              <ZCBullDetails
                key={key}
                bull={bull}
                selectedProof={key}
                selectedProofData={proofData?.proof}
                carcacaName={proofData?.carcacaName}
                hasCarcaca={proofData?.hasCarcaca}
                proofObject={proofObject}
              />
            );
          })}

      {!workableProofs?.length && (
        <Stack
          {...utils.stack.flex.col('center', 'center')}
          sx={utils.sx.container}
        >
          <Typography>Nenhuma avaliação encontrada</Typography>
        </Stack>
      )}

      <Stack
        {...utils.stack.flex.col('center', 'flex-start')}
        sx={{ width: '100%', flex: 1, overflowX: 'auto', overflowY: 'hidden' }}
      >
        <Pedigree bull={bull} />
      </Stack>
      {bull?.foot && bull.frame && bull.weight && bull.age && (
        <Box
          sx={{
            width: 'min-content',
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: '100vw',
          }}
        >
          <ObjectView
            data={{
              Frame: bull.frame,
              Peso: bull.weight,
              CE: bull.foot,
              Idade: bull.age,
            }}
          />
        </Box>
      )}
      {bull.thoracicPerimeter && <Measurements bull={bull} />}
    </Stack>
  );
};

export default BullDetails;
